import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { carePlanTableDataState } from "../../../components/recoil/carePlanDataRecoil";
import { getLocalTimeZoneDateFormat } from "../../../utils/dateutils";
import PatientProfileCard from "./patientcard/PatientProfileCard";
import TableActions from "./actions/TableActions";
import { useObservable } from "../../generic/hooks/useObservable";
import { formAction } from "./visitlist/VisitFormActions";
import { notification } from "antd";
import moment from "moment";
import Services from "./volunteerservices/Services";
import VolunteerCard from "./volunteerrequestcard/VolunteerCard";
import Frequency from "./visitlist/Frequency";
import CarePlanCardWrapper from "./CarePlanCardWrapper";
import apiService from "../../../service/apiservice/apiService";
import CarePlanTable from "./CarePlanTable";
import "./careplan.css";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { complianceFormTemplateCode } from "../../../components/recoil/complianceFormTemplateCodeRecoil";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { teamList } from "../../../components/recoil/teamsRecoil";
import { carePlanUpdateResponse } from "../../sidebar/notification/NotificationCard";
import { getFullName } from "../../careprovider/visits/utils";
import { searchKeywordAtom } from "../../../components/recoil/careplanStatusRecoil";
import CoordinatorCard from "./coordinator/CoordinatorCard";

const CARE_PLAN_TABLE_BREAKPOINT = 920;

const CarePlan = ({
  workforceIdentifier,
  patientIdentifier,
  coordinatorIdentifier,
  carePlanClass,
  spinnerClass,
  carePlanStatus,
}) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const user = useReadMemory(MemoryType.USER);
  const teams = useRecoilValue(teamList);
  const searchKeyword = useRecoilValue(searchKeywordAtom);
  const [tableData, setTableData] = useRecoilState(carePlanTableDataState);
  const setTemplateCode = useSetRecoilState(complianceFormTemplateCode);
  const [refresh, setRefresh] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [isTabletOrAbove, setIsTabletOrAbove] = useState(true);
  const [showPatientInfo, setShowPatientInfo] = useState(false);
  const [showVolunteerInfo, setShowVolunteerInfo] = useState(false);
  const containerRef = useRef(null);

  useObservable(formAction, setRefresh);
  useObservable(carePlanUpdateResponse, setRefresh);

  const allColumns = useMemo(
    () => [
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
        roles: ["Volunteer", "Volunteer Coordinator"],
      },
      {
        title: "Request Date",
        key: "requestDate",
        roles: ["Volunteer", "Volunteer Coordinator"],
        sorter: {
          compare: (a, b) => a.requestDate - b.requestDate,
        },
        render: (_, { requestDate, coordinator }) => (
          <>
            {moment(requestDate).format(getLocalTimeZoneDateFormat())}
            {coordinator && <CoordinatorCard coordinator={coordinator} />}
          </>
        ),
      },
      {
        title: "Patient/Recipient",
        key: "patienDetails",
        roles: ["Volunteer", "Volunteer Coordinator"],
        sorter: (a, b) => {
          const nameA =
            getFullName(a?.patient?.givenName, a?.patient?.familyName) || "";
          const nameB =
            getFullName(b?.patient?.givenName, b?.patient?.familyName) || "";
          // If both missing
          if (!nameA && !nameB) return 0;
          if (!nameA) return 1; // a missing → goes last in ascending
          if (!nameB) return -1; // b missing → goes last in ascending
          return nameA.localeCompare(nameB);
        },
        render: (_, { patient }) => (
          <PatientProfileCard
            patient={patient}
            showPatientInfo={showPatientInfo}
          />
        ),
      },
      {
        title: "Service",
        key: "service",
        roles: ["Volunteer", "Volunteer Coordinator"],
        render: (_, { services, status }) => (
          <Services services={services} status={status} direction="vertical" />
        ),
      },
      {
        title: "Volunteer",
        key: "volunteer",
        roles: ["Volunteer Coordinator"],
        render: (_, { opportunities, id }) => (
          <VolunteerCard
            opportunities={opportunities}
            carePlanId={id}
            showVolunteerInfo={showVolunteerInfo}
          />
        ),
      },
      {
        title: "Frequency",
        key: "frequency",
        roles: ["Volunteer", "Volunteer Coordinator"],
        render: (_, careplan) => <Frequency careplan={careplan} />,
      },
      {
        title: "Action",
        key: "action",
        roles: ["Volunteer", "Volunteer Coordinator"],
        render: (_, careplan) =>
          careplan?.patientIdentifier && <TableActions careplan={careplan} />,
      },
    ],
    [showPatientInfo, showVolunteerInfo],
  );

  const columns = allColumns.filter((column) =>
    column.roles.includes(user?.jobTitle),
  );

  const teamIdsParam = useMemo(() => {
    if (Array.isArray(teams) && teams.length > 0) {
      return teams
        .map((team) => team?.id)
        .filter((id) => id != null)
        .join(",");
    }
    return (user?.teams ?? [])
      .map((item) => item?.id)
      .filter((id) => id != null)
      .join(",");
  }, [teams, user]);

  useEffect(() => {
    const fetchCarePlanTableData = async () => {
      setTableLoading(true);

      const endpoint = "niramayafoundation/niramayavisits/api/v1/careplans";
      const params = {
        organizationIdentifier,
        pageSize: 500,
        page: 0,
        searchKeyword,
        carePlanStatus: carePlanStatus?.map((item) => item.value)?.join(","),
        teamIds: teamIdsParam,
        ...(workforceIdentifier && {
          workforceIdentifier,
        }),
        ...(patientIdentifier && {
          patientIdentifier,
        }),
        ...(coordinatorIdentifier && {
          coordinatorIdentifier,
        }),
        alerts: true,
      };

      const response = await apiService.get(endpoint, params);

      setTableData(response);
      setTableLoading(false);
    };

    fetchCarePlanTableData();
  }, [
    coordinatorIdentifier,
    organizationIdentifier,
    patientIdentifier,
    refresh,
    setTableData,
    workforceIdentifier,
    searchKeyword,
    carePlanStatus,
    teamIdsParam,
  ]);

  useEffect(() => {
    const getCareplanConfiguration = async () => {
      try {
        const payload = {
          configurationKey: [
            "niramaya.featureaccess.careplan.compliance",
            "niramaya.careplan.compliance.form",
            "niramaya.featureaccess.careplan.patient.info",
            "niramaya.featureaccess.careplan.volunteer.info",
          ],
        };
        const response = await executeIntent(
          IntentType.GET_CONFIGURATION,
          payload,
        );
        if (response[0].configurationValue && response[1].configurationValue) {
          const isComplianceAllowed = JSON.parse(
            response[0].configurationValue,
          )?.visibility;
          const formTemplateCode = JSON.parse(
            response[1].configurationValue,
          )?.templateCode;
          if (isComplianceAllowed) {
            setTemplateCode(formTemplateCode);
          }
        }
        if (response[2].configurationValue && response[3].configurationValue) {
          const isPatientInfoAllowed = JSON.parse(
            response[2].configurationValue,
          )?.visibility;
          const isVolunteerInfoAllowed = JSON.parse(
            response[3].configurationValue,
          )?.visibility;

          setShowPatientInfo(isPatientInfoAllowed);
          setShowVolunteerInfo(isVolunteerInfoAllowed);
        }
      } catch (err) {
        notification.error({
          message: "Error while getting form",
          placement: "bottomRight",
        });
      }
    };

    getCareplanConfiguration();
  }, [setTemplateCode]);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const target = element.parentElement ?? element;
    let frameId;

    const handleResize = () => {
      const parentWidth = target.getBoundingClientRect().width;
      setIsTabletOrAbove(parentWidth > CARE_PLAN_TABLE_BREAKPOINT);
    };

    const scheduleResize = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(handleResize);
    };

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(scheduleResize);

    observer?.observe(target);
    window.addEventListener("resize", scheduleResize);
    scheduleResize();

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      observer?.disconnect();
      window.removeEventListener("resize", scheduleResize);
    };
  }, []);

  return (
    <div className={carePlanClass} ref={containerRef}>
      {isTabletOrAbove ? (
        <CarePlanTable tableData={tableData} columns={columns} tableLoading={tableLoading} />
      ) : (
        <CarePlanCardWrapper
          tableData={tableData}
          showPatientInfo={showPatientInfo}
          tableLoading={tableLoading}
          spinnerClass={spinnerClass}
        />
      )}
    </div>
  );
};

export default CarePlan;
